import { createFileRoute, Link } from "@tanstack/react-router";
import {
  ArrowLeft,
  BookOpen,
  Download,
  HeartHandshake,
  ShieldCheck,
  Sparkles,
  Users,
} from "lucide-react";

const CANONICAL = "https://recoverytrauma.com/library/teen-boundaries-workbook";

export const Route = createFileRoute("/library/teen-boundaries-workbook")({
  head: () => ({
    meta: [
      {
        title:
          "Teen Boundaries Workbook (PDF) — Recovery Trauma™",
      },
      {
        name: "description",
        content:
          "A free, trauma-informed Teen Boundaries Workbook PDF from Recovery Trauma™ — practical prompts, scripts and downloadable exercises to help teenagers set and hold healthy boundaries.",
      },
      {
        name: "keywords",
        content:
          "teen boundaries workbook, boundaries workbook pdf, teen mental health, setting boundaries for teens, trauma-informed boundaries",
      },
      {
        property: "og:title",
        content: "Teen Boundaries Workbook (PDF) — Recovery Trauma™",
      },
      {
        property: "og:description",
        content:
          "Free trauma-informed Teen Boundaries Workbook PDF — prompts, scripts and downloadable exercises for teens learning to set boundaries.",
      },
      { property: "og:type", content: "article" },
      { property: "og:url", content: CANONICAL },
      { name: "twitter:card", content: "summary" },
    ],
    links: [{ rel: "canonical", href: CANONICAL }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Article",
          headline: "Teen Boundaries Workbook (PDF)",
          description:
            "A trauma-informed workbook helping teens understand, set and hold healthy boundaries.",
          author: {
            "@type": "Organization",
            name: "Recovery Trauma™",
          },
          publisher: {
            "@type": "Organization",
            name: "Recovery Trauma™",
          },
          mainEntityOfPage: CANONICAL,
        }),
      },
    ],
  }),
  component: TeenBoundariesWorkbookPage,
});

function TeenBoundariesWorkbookPage() {
  return (
    <div className="mx-auto max-w-3xl px-4 py-10 sm:px-6">
      <div className="mb-6">
        <Link
          to="/library"
          className="inline-flex items-center gap-2 text-sm font-medium text-foreground/70 hover:text-foreground"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Library
        </Link>
      </div>

      <header className="text-center">
        <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-[var(--gradient-gold)] shadow-[var(--shadow-gold)]">
          <BookOpen className="h-6 w-6 text-foreground" aria-hidden />
        </div>
        <p className="mt-4 text-xs font-medium uppercase tracking-[0.18em] text-[var(--gold-deep)]">
          Free Workbook · PDF
        </p>
        <h1 className="mt-2 font-display text-3xl tracking-tight text-foreground sm:text-4xl md:text-5xl">
          Teen Boundaries Workbook
        </h1>
        <p className="mx-auto mt-4 max-w-xl text-sm leading-relaxed text-foreground/75 sm:text-base">
          A trauma-informed workbook to help teenagers understand, name and hold
          healthy boundaries — with body, family, friends and online. Written
          plainly, with prompts you can actually use.
        </p>

        <div className="mt-6 flex flex-wrap items-center justify-center gap-3">
          <a
            href="mailto:hello@recoverytrauma.com?subject=Teen%20Boundaries%20Workbook%20PDF"
            className="inline-flex items-center gap-2 rounded-full bg-[var(--gold-deep)] px-5 py-2.5 text-sm font-semibold text-background shadow-[var(--shadow-gold)] transition-all hover:-translate-y-0.5"
            aria-label="Request the Teen Boundaries Workbook PDF by email"
          >
            <Download className="h-4 w-4" aria-hidden />
            Request the PDF
          </a>
          <Link
            to="/library"
            className="inline-flex items-center gap-2 rounded-full border border-gold/40 bg-cream/70 px-5 py-2.5 text-sm font-semibold text-[var(--gold-deep)] hover:border-gold/60"
          >
            More free resources
          </Link>
        </div>
      </header>

      <section aria-labelledby="what-inside" className="mt-12">
        <h2
          id="what-inside"
          className="font-display text-2xl tracking-tight text-foreground sm:text-3xl"
        >
          What's inside this teen boundaries workbook
        </h2>
        <p className="mt-3 text-sm leading-relaxed text-foreground/80 sm:text-base">
          Boundaries are how young people stay connected to themselves while
          staying in relationship with others. This PDF workbook walks teens
          through the language, the scripts and the practice — one small step at
          a time.
        </p>
        <ul className="mt-5 grid gap-3 sm:grid-cols-2">
          {[
            {
              icon: <HeartHandshake className="h-4 w-4" aria-hidden />,
              title: "Naming your yes and no",
              body: "Simple prompts to notice what actually feels okay in your body.",
            },
            {
              icon: <Users className="h-4 w-4" aria-hidden />,
              title: "Scripts for friends & family",
              body: "Short, plain-language phrases to try when saying no feels hard.",
            },
            {
              icon: <ShieldCheck className="h-4 w-4" aria-hidden />,
              title: "Online & social media limits",
              body: "Boundaries for group chats, screens and content that feels heavy.",
            },
            {
              icon: <Sparkles className="h-4 w-4" aria-hidden />,
              title: "Repair when it goes wrong",
              body: "What to do when a boundary gets crossed — or when you cross one.",
            },
          ].map((item) => (
            <li
              key={item.title}
              className="rounded-2xl border border-gold/25 bg-cream/40 p-4"
            >
              <div className="flex items-center gap-2 text-[var(--gold-deep)]">
                {item.icon}
                <span className="font-display text-sm font-semibold text-foreground">
                  {item.title}
                </span>
              </div>
              <p className="mt-1 text-sm leading-relaxed text-foreground/75">
                {item.body}
              </p>
            </li>
          ))}
        </ul>
      </section>

      <section aria-labelledby="who-for" className="mt-12">
        <h2
          id="who-for"
          className="font-display text-2xl tracking-tight text-foreground sm:text-3xl"
        >
          Who this workbook is for
        </h2>
        <p className="mt-3 text-sm leading-relaxed text-foreground/80 sm:text-base">
          Teens (roughly 13–18) working on trauma recovery, and the parents,
          carers, therapists and youth workers supporting them. It's designed to
          be gentle enough to use alone and structured enough to work through
          with a trusted adult.
        </p>
      </section>

      <section aria-labelledby="how-to-use" className="mt-12">
        <h2
          id="how-to-use"
          className="font-display text-2xl tracking-tight text-foreground sm:text-3xl"
        >
          How to use it
        </h2>
        <ol className="mt-3 list-decimal space-y-2 ps-5 text-sm leading-relaxed text-foreground/80 sm:text-base">
          <li>Download or request the PDF — it's free.</li>
          <li>Pick one section. You don't have to do it in order.</li>
          <li>
            Try one prompt today. Come back tomorrow. Small and slow is the
            point.
          </li>
          <li>
            If something surfaces that feels heavy, our{" "}
            <Link to="/crisis" className="underline">
              crisis support page
            </Link>{" "}
            lists free helplines.
          </li>
        </ol>
      </section>

      <aside className="mt-12 rounded-2xl border border-gold/30 bg-cream/60 p-5 text-sm leading-relaxed text-foreground/80">
        <p>
          Recovery Trauma™ resources are educational and peer-support tools —
          not a substitute for therapy, medical care or crisis services. If a
          young person is in immediate danger, contact local emergency services
          or a crisis line.
        </p>
      </aside>
    </div>
  );
}
